// The door-side readers of the identity and organisation values the app
// injects through `configureWorkspaceData` (ruling 65,
// `docs/monorepo-ui-floor-rulings/65-identity-crosses-by-injection.md`).
//
// Headless by construction: no design system, no router, no store, no `@/`.
// None of them reuses a name this door already publishes — a reader repointed
// onto `useMutateUpdateUser` or `useMutateUpdateSettings` would compile, pass
// every test and silently drop the delegate's reactions, which is what
// `@heidi/rules/no-same-name-door-codemod` refuses.

import {
  getWorkspaceDataConfig,
  type OrganizationInfoDelegate,
  type UpdateUserDelegate,
  type UpdateUserSettingsDelegate,
  type WorkspaceDataConfig,
} from './config';
// RELATIVE, and that is what makes the three composed readers below legal:
// `@heidi/rules/no-same-name-door-codemod` matches the DOOR specifier, so the
// cores are reached by path here and the ban on their names stays intact.
import {
  getDefaultDictationInputLanguages as getDefaultDictationInputLanguagesCore,
  getDefaultDictationOutputLanguages as getDefaultDictationOutputLanguagesCore,
  useIsReviewingOtherUser as useIsReviewingOtherUserCore,
  type DictationLanguageFlags,
} from './query/useQueryUser';

/**
 * Fails loudly and BY NAME. `getWorkspaceDataConfig` only knows whether the
 * seam was configured at all, so an un-injected member would otherwise reach a
 * consumer as `undefined` and blow up somewhere with no member in the message.
 */
const injected = <K extends keyof WorkspaceDataConfig>(
  member: K
): NonNullable<WorkspaceDataConfig[K]> => {
  const value = getWorkspaceDataConfig()[member];
  if (value === undefined) {
    throw new Error(
      `[workspace/data] \`${member}\` is not configured — the app injects it at ` +
        'src/utils/workspace-data-wiring.ts (ruling 65); a test exercising this ' +
        'reader must configure a double for that member first.'
    );
  }
  return value as NonNullable<WorkspaceDataConfig[K]>;
};

/**
 * The two dictation flags, resolved at CALL time. A function rather than a
 * hook because its readers — `getDefaultDictation*Languages` — run outside
 * React, and because a captured boolean would freeze a mid-session flip.
 */
export const readDictationLanguageFlags = (): DictationLanguageFlags =>
  injected('getDictationLanguageFlags')();

/**
 * The review-cohort member id the app's `selectedReviewMemberAtom` holds.
 * `useIsReviewingOtherUser` takes this value; this is where a package reader
 * gets it without importing the atom.
 */
export const useSelectedReviewMemberId = (): string | null | undefined =>
  injected('useSelectedReviewMemberId')();

/**
 * The three readers below COMPOSE an injected member with this package's own
 * core, so a consumer needs neither the flags nor the atom. None reuses the
 * app name: the move-plan generators classify a `src/hooks/query/useQuery*`
 * import per NAME against this door's export set, so an app-named export would
 * reclassify untouched app sites as door-forwarded in several committed
 * censuses — the reason `invalidateCurrentUserComposite` records below.
 */
export const readDefaultDictationInputLanguages = (): ReturnType<
  typeof getDefaultDictationInputLanguagesCore
> => getDefaultDictationInputLanguagesCore(readDictationLanguageFlags());

/** As above, for the output languages the transcribe `user_context` carries. */
export const readDefaultDictationOutputLanguages = (): ReturnType<
  typeof getDefaultDictationOutputLanguagesCore
> => getDefaultDictationOutputLanguagesCore(readDictationLanguageFlags());

/** The cohort verdict over the app's selected review member. */
export const useIsReviewingOtherUserForSelection = (): boolean =>
  useIsReviewingOtherUserCore(useSelectedReviewMemberId());

/** The app `useMutateUpdateUser` delegate, reactions included. */
export const useUpdateUser = (options?: {
  enableInvalidation?: boolean;
}): UpdateUserDelegate => injected('useUpdateUser')(options);

/** The app `useMutateUpdateSettings` delegate, reactions included. */
export const useUpdateUserSettings = (options?: {
  showToast?: boolean;
}): UpdateUserSettingsDelegate => injected('useUpdateUserSettings')(options);

/** The app `useCombinedOrganizationInfo` delegate, org-id write included. */
export const useOrganizationInfo = (): OrganizationInfoDelegate =>
  injected('useOrganizationInfo')();

/**
 * The app's COMPOSITE current-user invalidation (`GET_USER` + commerce's
 * `GET_PRODUCTS`), republished for consumers. D7 bans it as a workspace
 * REACTION and nothing in this package's `/data` calls it.
 *
 * NOT spelled `invalidateCurrentUser`: the move-plan generators classify a
 * `src/hooks/query/useQuery*` import per NAME against this door's export set,
 * so reusing the app name would reclassify that app path as door-forwarded in
 * seven domains' committed censuses without a reader having moved.
 */
export const invalidateCurrentUserComposite = (): Promise<void> =>
  injected('invalidateCurrentUser')();
